import { ChangeDetectionStrategy, Component, inject, Input } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { lastValueFrom, of } from 'rxjs';
import { MultiSelectModule } from 'primeng/multiselect';
import { InputSwitchChangeEvent, InputSwitchModule } from 'primeng/inputswitch';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { Pageable, UsersService } from '@src/api/auth';
import { BaseTablePageComponent } from '@src/app/components';
import { TableModule } from '@src/app/modules/table';
import { UserUI } from '@src/app/types';

import { FormType } from './types';
import { TABLE_NAME } from './constants';
import { StatusComponent, AddFormComponent } from './components';

@Component({
  selector: 'access-settings-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MultiSelectModule,
    InputSwitchModule,
    DialogModule,
    ButtonModule,
    TableModule,
    StatusComponent,
    AddFormComponent,
  ],
  templateUrl: './access-settings-form.component.html',
  styleUrl: './access-settings-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccessSettingsFormComponent extends BaseTablePageComponent<
  UserUI,
  UserUI,
  undefined,
  undefined,
  undefined,
  undefined,
  undefined,
  undefined
> {
  @Input() loading = false;
  @Input() override form!: FormGroup<FormType>;

  private readonly usersService = inject(UsersService);

  confirmDialogVisible = false;

  constructor() {
    super({
      useQueryParams: false,
      tableName: TABLE_NAME,
      dataKey: 'id',
      toolbarOptions: {
        visible: true,
        buttons: {
          add: true,
          delete: true,
          columns: false,
        },
      },
      rowMenuOptions: {
        visible: true,
        buttons: {
          delete: true,
        },
      },
      columnOptions: {
        sorting: true,
      },
      addDialogOptions: {
        header: 'Добавление пользователей',
      },
      selecting: true,
      currentPageReportTemplate: 'Отображено с {first} по {last} из {totalRecords} пользователей',
      columns: ['isEnabled', 'firstName', 'surName', 'lastName', 'position'],
      excludeColumns: ['username', 'phone', 'email', 'role', 'createdAt', 'updatedAt', 'loginAt'],
      sortField: 'id',
    });
  }

  get isPublicControl() {
    return this.form.controls.isPublic;
  }

  get isPublicValue() {
    return this.isPublicControl.value;
  }

  get grantedUsersControl() {
    return this.form.controls.grantedUsers;
  }

  get grantedUsersValue() {
    return this.grantedUsersControl.value;
  }

  onChangeIsPublic(event: InputSwitchChangeEvent) {
    if (event.checked && this.grantedUsersValue.length > 0) {
      this.confirmDialogVisible = true;
    }
  }

  getRequestObservable(pageable?: Pageable, filter?: { [key: string]: Array<any> }) {
    if (this.grantedUsersValue.length) {
      const uuidFilter = this.grantedUsersValue.map(uuid => {
        return {
          value: uuid,
          matchMode: 'equals',
          operator: 'or',
        };
      });

      if (!filter) {
        filter = {};
      }

      filter['uuid'] = uuidFilter;

      return this.usersService.getUsersByFilter(pageable, filter);
    } else {
      return of([]);
    }
  }

  protected override onAdd() {
    const newUserUUIDs = this.addData?.map(user => user.uuid!).filter(uuid => !!uuid) ?? [];
    this.grantedUsersControl.setValue([...this.grantedUsersValue, ...newUserUUIDs]);
    return lastValueFrom(of(this.addData));
  }

  protected override async onDelete(items: UserUI[]): Promise<void> {
    const deletedUUIDs = items?.map(user => user.uuid!).filter(uuid => !!uuid) ?? [];
    const newUserUUIDs = this.grantedUsersValue.filter(uuid => !deletedUUIDs.includes(uuid));
    this.grantedUsersControl.setValue(newUserUUIDs);

    return;
  }

  addSelectedUUIDsChange(value: UserUI[]) {
    this.addData = value;
  }

  confirmDialogResult(res: boolean) {
    if (res) {
      this.grantedUsersControl.setValue([]);
    } else {
      this.isPublicControl.setValue(false);
    }

    this.confirmDialogVisible = false;
    this.cdr.markForCheck();
  }
}
